import { I18n, t } from 'src/libs/i18n';

import {
  MACHINING_SELECT_CONDITION,
  INPUT_INDEX,
} from 'src/constants/quotation/conditionInput/conditionMachining';
import {
  UNIT_OPTIONS,
  ROLL_EYES_SPECIAL_CONDITION,
  MILLSHEET_OPTIONS,
  SHAPE_TYPE,
} from 'src/constants/quotation/optionSelect';
import { CHAMFER_CONDITION } from 'src/constants/quotation/conditionInput/conditionChamfer';
import { CORNER_CONDITION } from 'src/constants/quotation/conditionInput/conditionCorner';
import { ROLL_EYES_CONDITION } from 'src/constants/quotation/conditionInput/conditionRolleyes';
import { IMAGE_PREVIEW } from 'src/constants/quotation/imagePreview';
import { productFormat } from 'src/utils/product';
import { isAllTrue } from './helper';
import { ROUND } from 'src/constants/app';

type FormatSizesType = {
  thickness: string;
  width?: string;
  length?: string;
  dimension4?: string;
  dimension5?: string;
  dimension6?: string;
};

/**
 * Check condition show machining select box
 * @param shapeType: shape type user selected
 * @param machiningMethod: machining method user selected
 * @param selectName: name of select box want to check show
 * @returns [show, active]
 */
export const getVisiableMachining = (
  shapeType: string,
  machiningMethod: string,
  selectName: string,
) => {
  const shapeCondition = MACHINING_SELECT_CONDITION.filter(
    (item) => item.shapeType == shapeType,
  )[0];
  if (!shapeCondition) return false;
  var machiningCondition = shapeCondition.condition.filter(
    (item) => item.matchiningType == machiningMethod,
  )[0];
  if (!machiningCondition) {
    machiningCondition = shapeCondition.condition.filter(
      (item) => item.matchiningType == 'Others',
    )[0];
  }
  const indexSelect = INPUT_INDEX.filter((item) => item.value == selectName)[0];
  if (!indexSelect) return false;
  return isAllTrue(machiningCondition.condition[indexSelect.index]);
};

/**
 * Check condition show Chamfer select Box
 * @param shapeType: shape type user selected
 * @param machiningMethod: machining method user selected
 * @param indexInput: position of input want to show
 * returns {show, active}
 */
export const getVisiableChamfer = (
  shapeType: string,
  machiningMethod: string,
  indexInput: number,
) => {
  const shapeCondition = CHAMFER_CONDITION.filter(
    (item) => item.shapeType == shapeType,
  )[0];
  if (!shapeCondition) return false;
  var machiningCondition = shapeCondition.condition.filter(
    (item) => item.matchiningType == machiningMethod,
  )[0];
  if (!machiningCondition) {
    machiningCondition = shapeCondition.condition.filter(
      (item) => item.matchiningType == 'Others',
    )[0];
  }
  return isAllTrue(machiningCondition.condition[indexInput]);
};

/**
 * Check condition show Corner Box
 * @param shapeType: shape type user selected
 * @param machiningMethod: machining method user selected
 * returns {show, active}
 */
export const getVisiableCorner = (
  shapeType: string,
  machiningMethod: string,
) => {
  const shapeCondition = CORNER_CONDITION.filter(
    (item) => item.shapeType == shapeType,
  )[0];
  if (!shapeCondition) return false;
  var machiningCondition = shapeCondition.condition.filter(
    (item) => item.matchiningType == machiningMethod,
  )[0];
  if (!machiningCondition) {
    machiningCondition = shapeCondition.condition.filter(
      (item) => item.matchiningType == 'Others',
    )[0];
  }
  return isAllTrue(machiningCondition.condition);
};

/**
 * Check condition show RollEyes select Box
 * @param shapeType: shape type user selected
 * @param varietyType: variety type user selected
 * returns {show, active}
 */
export const getVisiableRollEyes = (
  shapeType: string,
  varietyType: string,
  machiningMethod: string,
  product: string,
) => {
  /**
   * If product have hakudoShhnCd and don't have shhnCd then compare condition
   * Otherwise deactive rolleyes.
   */
  const productSplit = productFormat.splitProductId(product);
  const conditionProduct =
    (!productSplit.hakudoShhnCd && productSplit.shhnCd) ||
    (productSplit.hakudoShhnCd && productSplit.shhnCd);

  if (conditionProduct && shapeType == SHAPE_TYPE.plate) {
    return false;
  }
  // condition special machining method
  const specialCondition = ROLL_EYES_SPECIAL_CONDITION.conditionMachining.some(
    (machinning) => machiningMethod?.includes(machinning),
  );
  if (shapeType == ROLL_EYES_SPECIAL_CONDITION.variety && specialCondition) {
    return false;
  }
  // condition shape and machining method
  const shapeCondition = ROLL_EYES_CONDITION.filter(
    (item) => item.shapeType == shapeType,
  )[0];
  if (!shapeCondition) return false;
  let varietyCondition = shapeCondition.condition.filter(
    (item) => item.varietyType == varietyType,
  )[0];
  if (!varietyCondition) {
    varietyCondition = shapeCondition.condition.filter(
      (item) => item.varietyType == 'Others',
    )[0];
  }
  return isAllTrue(varietyCondition?.condition);
};

/**
 * Get patch image by name
 * @param code: code of shape type
 */
export const getProductUnit = (code: string) => {
  const unit = UNIT_OPTIONS.filter((item) => item.value == code)[0];
  if (!unit) return null;

  return I18n.t(`pricing_page.${unit?.label}`);
};

/**
 * Get patch image by name
 * @param name: name of image want to get path
 */
export const getImagePreviewPath = (name: string) => {
  const imagePreview = IMAGE_PREVIEW.filter((item) => item.name == name)[0];
  if (!imagePreview) return '';
  return imagePreview?.image;
};

export const getImageIconPreviewPath = (name?: string) => {
  const imagePreview = IMAGE_PREVIEW[IMAGE_PREVIEW.length - 1];
  return imagePreview?.image || '';
};

/**
 * format sizes to string AxBxC
 */
export const formatSizes = (param: FormatSizesType) => {
  const { thickness, width, length } = param;
  return [thickness, width, length].filter(Boolean).join('x');
};

/**
 * Converts thickness, width, and length values to their fractionDisplay equivalents using given fractionsData.
 * - If input is 'x'-separated, processes each part separately.
 * - If match found in fractionsData, replaces with fractionDisplay.
 * - If not found, uses original value.
 * - Concatenates processed values with 'x'.
 */
export const formatFractionSize = (
  {
    thickness,
    width,
    length,
  }: {
    thickness?: string | number;
    width?: string | number;
    length?: string | number;
  },
  fractionsData: Response.Fractions[],
  decimalInputSizeFlags?: Payload.DecimalInputSizeFlags,
): string => {
  // Helper to map a value to its fractional display if conversion is enabled
  const toFractionDisplay = (
    value: string | number | undefined,
    shouldConvert: boolean,
  ): string => {
    if (value == null || value === '') return '';
    const strValue = typeof value === 'number' ? value.toString() : value;
    if (!shouldConvert) return strValue;
    const found = fractionsData.find(
      (fd) =>
        Math.abs(parseFloat(fd.decimalDisplay) - parseFloat(strValue)) < 0.0001,
    );
    return found ? found.fractionDisplay : strValue;
  };

  // Process thickness which may be multiple 'x'-parts
  const thicknessParts =
    thickness == null || thickness === ''
      ? []
      : String(thickness)
          .split('x')
          .map((part) =>
            toFractionDisplay(
              part.trim(),
              Boolean(decimalInputSizeFlags?.isSnpo1BunsuHyoji),
            ),
          );

  // Process width and length, single values
  const widthValue = toFractionDisplay(
    width,
    Boolean(decimalInputSizeFlags?.isSnpo2BunsuHyoji),
  );
  const lengthValue = toFractionDisplay(
    length,
    Boolean(decimalInputSizeFlags?.isSnpo3BunsuHyoji),
  );

  // Build result, skipping empty values
  return [
    ...thicknessParts,
    ...(widthValue ? [widthValue] : []),
    ...(lengthValue ? [lengthValue] : []),
  ].join('x');
};

export const getMillSheet = (optc091) => {
  const optc091Str = MILLSHEET_OPTIONS(t).find(
    (item) => item.value == (optc091 || ''),
  )?.label;
  return optc091Str;
};

/**
 * format details order and matching label
 * @returns string (a/b/c)
 */
export const getDetailOrderString = (
  kyaksChmnNo,
  opt,
  optc091,
  biko,
  isProductFinished,
) => {
  const optc091Str = !isProductFinished && getMillSheet(optc091);
  return [kyaksChmnNo, opt, optc091Str, biko].filter(Boolean).join('/');
};

/**
 * get 3 param from finishingSize to fill to each input re-estimate.
 */
export const getSizeByLabelInput = (
  manufacturingSizes,
  finishingSizeArr,
  isProductFinished,
) => {
  const sizeLength = manufacturingSizes.snpoSize || 0;
  const param1 = finishingSizeArr.slice(0, sizeLength)?.join('x');
  const param2 = finishingSizeArr.slice(sizeLength, sizeLength + 1).join('');
  const param3 = finishingSizeArr
    .slice(sizeLength + 1, sizeLength + 2)
    .join('');
  return {
    param1: !isProductFinished
      ? normalizeValueByUnit(param1, manufacturingSizes)
      : Number(parseFloat(param1).toFixed(ROUND.TO_5_DIGIT)).toString(),
    param2:
      param2 && Number(parseFloat(param2).toFixed(ROUND.TO_5_DIGIT)).toString(),
    param3:
      param3 && Number(parseFloat(param3).toFixed(ROUND.TO_5_DIGIT)).toString(),
  };
};

export const normalizeValueByUnit = (param1, manufacturingSizes): string => {
  const valueParts = param1.split('x');
  const unitParts = manufacturingSizes.unitSize1?.split('x');

  const normalizedParts = valueParts.map((value, index) => {
    if (unitParts?.[index] !== manufacturingSizes.motoTaniKubun) {
      const rounded = Number(
        parseFloat(value).toFixed(ROUND.TO_5_DIGIT),
      ).toString();
      return rounded;
    }
    return value;
  });

  return normalizedParts.join('x');
};

export const getLabelDimension = (snpoGuide: string): string => {
  const parts = snpoGuide.split('x');
  return parts.map((part, index) => `${part} (${index + 1})`).join(' x ');
};

export const processFormatAndValues = (
  formatStr: string,
  valueStr: string,
  originalValueStr: string,
  num: number,
): {
  formatParts: string[];
  valueParts: string[];
  originalValueParts: string[];
  sizeInputNum: number;
} => {
  const formatParts = formatStr?.split('x');
  const segmentCount = formatParts?.length;
  let sizeInputNum = segmentCount - num + 1;

  // If valueStr is empty, return valueParts as an empty array
  if (!valueStr)
    return {
      formatParts,
      valueParts: [],
      originalValueParts: [],
      sizeInputNum,
    };

  const rawValues = valueStr
    .split('x')
    .map((val) => Number(parseFloat(val).toFixed(ROUND.TO_5_DIGIT)).toString());

  const rawOriginalValues = originalValueStr
    .split('x')
    .map((val) => Number(parseFloat(val).toFixed(ROUND.TO_5_DIGIT)).toString());

  const valueParts: string[] = [];
  const originalValueParts: string[] = [];

  // Join the first 'num' elements with 'x' if num is valid
  if (num >= 1 && num <= rawValues.length) {
    const joined = rawValues.slice(0, num).join('x');
    valueParts.push(joined);
    valueParts.push(...rawValues.slice(num));

    const joinedOriginalValue = rawOriginalValues.slice(0, num).join('x');
    originalValueParts.push(joinedOriginalValue);
    originalValueParts.push(...rawOriginalValues.slice(num));
  } else {
    // If num is invalid, keep the original split
    valueParts.push(...rawValues);
    originalValueParts.push(...rawOriginalValues);
  }

  return { formatParts, valueParts, originalValueParts, sizeInputNum };
};

export const formatSnpoGuide = (
  snpoGuide: string,
  snpoSize: number,
): string[] => {
  const parts = (snpoGuide || '').split('x').map((p) => p.trim());

  // If snpoSize is valid and within bounds, join first snpoSize parts
  if (snpoSize >= 1 && snpoSize <= parts.length) {
    const first = parts.slice(0, snpoSize).join('x');
    const rest = parts.slice(snpoSize);
    return [first, ...rest];
  }

  // If snpoSize is invalid, return all parts as is
  return parts;
};

export const formatDecimal = (input: string): string => {
  // Check if the string can be converted to a valid number
  const num = Number(input);

  if (isNaN(num)) {
    return input; // Not a number => return the original string
  }

  // Check if it's a decimal number (contains a dot and decimal part)
  if (input.includes('.') && input.split('.')[1].length > 0) {
    const [integerPart, decimalPart] = input.split('.');
    const trimmedDecimal = decimalPart.slice(0, 4); // Truncate to 4 digits after the dot
    return `${integerPart}.${trimmedDecimal}`;
  }

  return input; // Not a decimal number => return as is
};
